import React, { useMemo, VFC } from 'react'

import { Maybe, SanityAddress, SanityContact } from '../../../groq/sanity-dispensaries/types'
import useIsSsr from '../../../hooks/useIsSsr'
import { Dispensary, getDispensaryBasePath } from '../../../lib/dispensaries'
import { dispensaryImageUrlFor } from '../../../lib/sanity'
import * as Styled from './styled'

import addressIcon from '../../../img/address-svgrepo-com.svg'
import defaultPicture from '../../../img/doctor.svg'
import mailIcon from '../../../img/mail-svgrepo-com.svg'
import phoneIcon from '../../../img/phone-svgrepo-com.svg'
import webIcon from '../../../img/web-svgrepo-com.svg'

export interface DispensaryContactCardContentProps {
  contact: SanityContact | null | undefined
  dispensary: Pick<Dispensary, 'name' | 'slug'>
  clickableLinks?: boolean
}

const parseSpecialties = (specialties: string[] | null | undefined): string => {
  if (!specialties || !specialties.length) {
    return ''
  }

  switch (specialties.length) {
    case 1:
      return `${specialties[0]}`
    case 2:
      return `${specialties[0]} & ${specialties[1]}`
    default:
      // ignore remaining specialties
      return `${specialties[0]}, ${specialties[1]}, & ${specialties[2]}`
  }
}

const parseAddressParts = (address: SanityAddress): string[] => {
  return [
    ...(address.street?.map((street) => street.trim()) || []),
    address.city?.trim() || '',
    `${address.region?.trim() || ''}${
      (address.region?.trim() && address.postcode?.trim() && ' ') || ''
    }${address.postcode?.trim() || ''}`,
    address.countryCode?.trim() || '',
  ].filter(Boolean)
}

const parseAddress = (address: Maybe<SanityAddress> | undefined): JSX.Element | null => {
  if (!address) {
    return null
  }

  const addressArray = parseAddressParts(address)
  return (
    <>
      {addressArray.reduce((acc: Array<JSX.Element | string>, component, index) => {
        if (acc.length) {
          acc.push(', ')
          acc.push(<wbr key={`wbr-${index}`} />)
        }
        acc.push(
          <span style={{ whiteSpace: 'nowrap' }} key={`c-${index}`}>
            {component}
          </span>,
        )
        return acc
      }, [])}
    </>
  )
}

export const DispensaryContactCardContent: VFC<DispensaryContactCardContentProps> = React.memo(
  ({ contact, dispensary, clickableLinks }) => {
    const parsedContact = useMemo(
      () => ({
        specialties: parseSpecialties(contact?.specialties),
        address: parseAddress(contact?.address),
        email: contact?.email?.trim(),
        phone: contact?.phone?.trim(),
        blurb: contact?.blurb?.trim(),
        imageRef: contact?.image?.asset._ref,
      }),
      [contact],
    )
    const isSsr = useIsSsr()

    const googleMapsLinkQuery = useMemo(
      (): string => (contact?.address ? parseAddressParts(contact?.address).join(', ') : ''),
      [contact?.address],
    )

    const dispensaryPath = getDispensaryBasePath(dispensary.slug)

    return (
      <>
        <Styled.TitleContainer>
          {parsedContact.imageRef ? (
            <Styled.DispensaryPicture
              src={
                dispensaryImageUrlFor(parsedContact.imageRef)
                  .ignoreImageParams()
                  .width(60)
                  .height(60)
                  .fit('clip')
                  .dpr(3)
                  .auto('format')
                  .url() || ''
              }
            />
          ) : (
            <Styled.DispensaryPicture src={defaultPicture} />
          )}
          <Styled.DisplayColumn>
            <Styled.DispensaryName>{dispensary.name || dispensary.slug}</Styled.DispensaryName>
            {parsedContact.specialties && (
              <Styled.DispensarySpecialty>{parsedContact.specialties}</Styled.DispensarySpecialty>
            )}
          </Styled.DisplayColumn>
        </Styled.TitleContainer>
        <Styled.InfoContainer>
          <Styled.InfoList>
            {parsedContact.address && (
              <Styled.InfoItem>
                {clickableLinks ? (
                  <Styled.InfoLink
                    to={`https://maps.google.com/?q=${googleMapsLinkQuery}`}
                    target="_blank"
                  >
                    <Styled.InfoIcon src={addressIcon} />
                    <Styled.InfoText>{parsedContact.address}</Styled.InfoText>
                  </Styled.InfoLink>
                ) : (
                  <>
                    <Styled.InfoIcon src={addressIcon} />
                    <Styled.InfoText>{parsedContact.address}</Styled.InfoText>
                  </>
                )}
              </Styled.InfoItem>
            )}
            {parsedContact.email && (
              <Styled.InfoItem>
                {clickableLinks ? (
                  <Styled.InfoLink to={`mailto:${parsedContact.email}`}>
                    <Styled.InfoIcon src={mailIcon} />
                    <Styled.InfoText>{parsedContact.email}</Styled.InfoText>
                  </Styled.InfoLink>
                ) : (
                  <>
                    <Styled.InfoIcon src={mailIcon} />
                    <Styled.InfoText>{parsedContact.email}</Styled.InfoText>
                  </>
                )}
              </Styled.InfoItem>
            )}
            {parsedContact.phone && (
              <Styled.InfoItem>
                {clickableLinks ? (
                  <Styled.InfoLink to={`tel:${parsedContact.phone}`}>
                    <Styled.InfoIcon src={phoneIcon} />
                    <Styled.InfoText>{parsedContact.phone}</Styled.InfoText>
                  </Styled.InfoLink>
                ) : (
                  <>
                    <Styled.InfoIcon src={phoneIcon} />
                    <Styled.InfoText>{parsedContact.phone}</Styled.InfoText>
                  </>
                )}
              </Styled.InfoItem>
            )}
            {!isSsr && (
              <Styled.InfoItem>
                {clickableLinks ? (
                  <Styled.InfoLink to={dispensaryPath} forceBase>
                    <Styled.InfoIcon src={webIcon} />
                    <Styled.InfoText>{`${window.location.origin}${dispensaryPath}`}</Styled.InfoText>
                  </Styled.InfoLink>
                ) : (
                  <>
                    <Styled.InfoIcon src={webIcon} />
                    <Styled.InfoText>{`${window.location.origin}${dispensaryPath}`}</Styled.InfoText>
                  </>
                )}
              </Styled.InfoItem>
            )}
          </Styled.InfoList>
          {parsedContact.blurb && (
            <Styled.PractitionerBlurb>{parsedContact.blurb}</Styled.PractitionerBlurb>
          )}
        </Styled.InfoContainer>
      </>
    )
  },
)

DispensaryContactCardContent.displayName = 'DispensaryContactCardContent'
